// Copied from apps/admin/src/features/gps-stops/ui/gps-stops-legend.tsx (PRD #875).
import React, { useMemo } from "react";
import { useT, Namespace } from "@racoon/i18n";
import { Checkbox } from "@racoon/ui";
import type { GpsStop } from "../model/useGpsStops";
import {
  GPS_STOP_LEGEND_GROUPS,
  PICKUP_LOCATION_AWARE_TYPES,
  type GpsStopLegendGroup,
  type GpsStopType,
} from "../model/stop-types";

/** Small shape swatch mirroring the marker drawn on the map. */
const TypeSwatch: React.FC<{ group: GpsStopLegendGroup }> = ({ group }) => {
  const { color, shape } = group;
  const style: React.CSSProperties = { width: 10, height: 10, flex: "0 0 auto" };

  if (shape === "circle") Object.assign(style, { background: color, borderRadius: "50%" });
  else if (shape === "diamond")
    Object.assign(style, { background: color, transform: "rotate(45deg)" });
  else if (shape === "square") Object.assign(style, { background: color, borderRadius: 2 });
  else if (shape === "triangle")
    Object.assign(style, {
      width: 0,
      height: 0,
      borderLeft: "5px solid transparent",
      borderRight: "5px solid transparent",
      borderBottom: `10px solid ${color}`,
    });
  else Object.assign(style, { border: `2px solid ${color}`, borderRadius: "50%" });

  return <span style={style} />;
};

export interface GpsStopsLegendProps {
  /** Master on/off for the whole layer. */
  enabled: boolean;
  onEnabledChange: (enabled: boolean) => void;
  visibleTypes: Set<GpsStopType>;
  onVisibleTypesChange: (types: Set<GpsStopType>) => void;
  /** Stops currently loaded, used for the per-type counts. */
  stops: GpsStop[];
  isFetching?: boolean;
  /** Prefix for checkbox ids, so two legends on one page stay distinct. */
  idPrefix?: string;
}

/**
 * Checkbox legend for the GPS stops layer. Drop it into whatever settings
 * popover the host map already has.
 */
export const GpsStopsLegend: React.FC<GpsStopsLegendProps> = ({
  enabled,
  onEnabledChange,
  visibleTypes,
  onVisibleTypesChange,
  stops,
  isFetching = false,
  idPrefix = "gps-stops",
}) => {
  const t = useT();
  const counts = useMemo(() => {
    const acc = new Map<GpsStopType, number>();
    for (const stop of stops) acc.set(stop.type, (acc.get(stop.type) ?? 0) + 1);
    return acc;
  }, [stops]);

  const toggleGroup = (group: GpsStopLegendGroup, checked: boolean) => {
    const next = new Set(visibleTypes);
    for (const type of group.types) {
      if (checked) next.add(type);
      else next.delete(type);
    }
    onVisibleTypesChange(next);
  };

  // Whether the pickup-location detector covered this window at all. Some orgs
  // (org 43 among them) have stops only from the older producer, which matched
  // against depots and landfills and never looked at pickup locations — so a
  // truck parked 90 m from a container site still came out as "Подозрительная".
  // Left unsaid, an inspector reads that as "did not service the site", which on
  // a screen with a "В список подозреваемых" button is a costly misreading.
  const hasPickupLocationClassification = useMemo(
    () =>
      stops.some(
        (s) => s.source === "job_stop_detector" || PICKUP_LOCATION_AWARE_TYPES.includes(s.type),
      ),
    [stops],
  );

  // Every type is fetched regardless of what is checked, so a 0 here truthfully
  // means "none found" rather than "not loaded".
  const rows = GPS_STOP_LEGEND_GROUPS.map((group) => ({
    group,
    count: group.types.reduce((sum, type) => sum + (counts.get(type) ?? 0), 0),
  })).filter(({ group, count }) => count > 0 || group.alwaysShown);

  return (
    <div className="space-y-3">
      <div className="flex items-center space-x-2">
        <Checkbox
          id={`${idPrefix}-enabled`}
          checked={enabled}
          onCheckedChange={(checked) => onEnabledChange(!!checked)}
        />
        <label
          htmlFor={`${idPrefix}-enabled`}
          className="font-medium text-sm cursor-pointer select-none"
        >
          {t(Namespace.GpsTracking, "gpsStopsTitle")}
        </label>
      </div>

      {enabled && (
        <div className="space-y-2 border-l border-gray-200 pl-2">
          {isFetching && (
            <div className="text-xs text-text-secondary">
              {t(Namespace.GpsTracking, "gpsStopsLoading")}
            </div>
          )}
          {!hasPickupLocationClassification && stops.length > 0 && (
            <div className="rounded bg-amber-50 px-2 py-1.5 text-xs leading-snug text-amber-900">
              {t(Namespace.GpsTracking, "gpsStopsNoSiteMatchWarning")}
            </div>
          )}
          {rows.map(({ group, count }) => (
            <div key={group.key} className="flex items-center space-x-2">
              <Checkbox
                id={`${idPrefix}-${group.key}`}
                checked={group.types.every((type) => visibleTypes.has(type))}
                onCheckedChange={(checked) => toggleGroup(group, !!checked)}
              />
              <TypeSwatch group={group} />
              <label
                htmlFor={`${idPrefix}-${group.key}`}
                className="text-sm cursor-pointer select-none flex-1"
              >
                {t(Namespace.GpsTracking, group.labelKey)}
              </label>
              <span className="text-xs text-text-secondary tabular-nums">{count}</span>
            </div>
          ))}
        </div>
      )}
    </div>
  );
};
